import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider, useForm } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import {
  Checkbox,
  FormDialog,
  FormLayout,
  FormSection,
  FormRow as Row,
  TextInput,
} from '~/components';
import { TermsContent } from '~/pages/Terms';
import { logger } from '~/utils/logger';
import { NextButton } from '../components/Layout';
import { employerRegistrationView } from '../registration-view.machine';
import { WelcomeSchema, WelcomeValues } from '../types';

const TAGS = CT.registration.welcome;

const log = logger.child('WelcomeForm');

interface WelcomeFormProps {
  actorRef: ActorRefFrom<typeof employerRegistrationView>;
  defaultValues?: WelcomeValues;
  loading?: boolean;
}

const ACCEPT_TOS_LABEL =
  'I confirm that I have read and accept the terms and conditions and privacy policy';

export const WelcomeForm = ({ actorRef, defaultValues }: WelcomeFormProps) => {
  const { tosModalOpen } = useSelector(actorRef, (state) => state.context.welcomeForm);

  const methods = useForm<WelcomeValues>({
    defaultValues: defaultValues || {
      email: '',
      tosAccepted: false,
    },
    resolver: zodResolver(WelcomeSchema),
    mode: 'onBlur',
  });

  const onSubmitSuccess = (data: WelcomeValues) => {
    log.debug('Welcome Form Submit: ', data);
    actorRef.send({
      type: 'ON_SUBMIT_WELCOME',
      data,
    });
  };

  const onSubmitError = (err: unknown) => {
    log.warn('Welcome Form Submit Error: ', err);
  };

  const handleOpenTos = () => actorRef.send({ type: 'ON_OPEN_MODAL' });
  const handleCloseTos = () => actorRef.send({ type: 'ON_CLOSE_MODAL' });

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col h-full"
        onSubmit={methods.handleSubmit(onSubmitSuccess, onSubmitError)}
      >
        {/* [MCV-1577] FormSection IS the Fieldset this step used to hand-roll
            (see components/forms/FormSection.tsx), so the step now stacks its
            fields at the template's 20px instead of a private 16px, matching
            the EmployerInfoForm step that follows it. */}
        <FormLayout>
          <FormSection componentsPerLine={1}>
            <Row>
              <TextInput name={'email'} cyTag={''} label={'Email address'} required />
            </Row>
            <Row>
              <div
                data-cy={TAGS.tosModalOpen}
                onClick={handleOpenTos}
                className="h-50 w-full overflow-y-scroll cursor-pointer rounded-md border border-grey-900 bg-white px-4 py-3 text-left"
              >
                <TermsContent />
              </div>
              <Checkbox
                name={'tosAccepted'}
                cyTag={TAGS.tosToggle}
                errorCyTag={TAGS.tosError}
                style="border border-grey-800 border-1"
                label={ACCEPT_TOS_LABEL}
                rowReverse={true}
                required
              />
            </Row>
          </FormSection>
        </FormLayout>
        {/* The form keeps its own `flex flex-col h-full` and NextButton keeps
            its `flex-1`: together they pin Next to the bottom of FormContent's
            fixed-height scroll box. Relocating this button into the page action
            row (as the provider twin already does via <Footer next={...} />)
            needs a `next` prop on this feature's Layout Footer, which is a
            separate change. */}
        <NextButton cyTag={TAGS.nextButton} />

        <FormDialog
          creating={false}
          editing={tosModalOpen}
          onClose={handleCloseTos}
          title={'Terms of Service'}
        >
          <div className="overflow-y-auto max-h-[70vh] px-4 py-3">
            <TermsContent />
          </div>
          <div className="px-4 pt-3 pb-2">
            <Checkbox
              name={'tosAccepted'}
              controlId="tosAccepted-dialog"
              cyTag={TAGS.tosToggle}
              style="border border-grey-800 border-1"
              label={ACCEPT_TOS_LABEL}
              rowReverse={true}
              required
            />
          </div>
        </FormDialog>
      </form>
    </FormProvider>
  );
};
